import {
  ItemCancellationView,
  ItemSalesSummaryView,
  OrderSettlementView,
  OutletSalesReport,
  SalesReportCreditorView,
  SalesReportDiscountView,
  SalesReportOrderView,
} from '@/renderer/types/report';
import { SalesReportModalProps } from '@/renderer/types/sales';
import { Download, Mail, ReceiptText, X } from 'lucide-react';
import React, { useMemo } from 'react';
import { Button } from '../common';
import { LoadingAnimation } from '../common/LoadingAnimation';

const formatAmount = (value: number): string => value.toLocaleString();

const formatBusinessDayLabel = (value: string | null | undefined): string => {
  if (!value) return 'Selected business day';

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return parsedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const formatSettlementTime = (value: string): string => {
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return parsedDate.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatUserName = (
  user:
    | OrderSettlementView['created_by']
    | SalesReportDiscountView['added_by']
    | SalesReportCreditorView['created_by']
    | SalesReportOrderView['created_by'],
): string => {
  return (
    [user.first_name, user.last_name].filter(Boolean).join(' ') || 'System'
  );
};

const formatLocation = (value: {
  table_name: string | null;
  section_name?: string | null;
  mode: string;
}): string => {
  const primary =
    value.table_name || (value.mode === 'DINE' ? 'TABLE' : 'ORDER');
  if (!value.section_name) return primary;
  return `${primary} • ${value.section_name}`;
};

const formatOrderParty = (order: SalesReportOrderView): string => {
  if (order.client_name && order.waiter_name) {
    return `${order.client_name} • ${order.waiter_name}`;
  }

  return order.client_name || order.waiter_name || '-';
};

const ItemSalesByDepartmentSection: React.FC<{
  items: ItemSalesSummaryView[];
}> = ({ items }) => {
  const grouped = useMemo(() => {
    const map = items.reduce<Map<string, ItemSalesSummaryView[]>>(
      (acc, item) => {
        const key = item.department_name;
        const existing = acc.get(key);
        if (existing) existing.push(item);
        else acc.set(key, [item]);
        return acc;
      },
      new Map(),
    );
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [items]);

  return (
    <section className="overflow-hidden rounded-md border border-gray-200">
      <div className="app-bg border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
          Item Sales by Department
        </h3>
        <p className="mt-0.5 text-xs text-black/70">
          Individual items sold, grouped by department.
        </p>
      </div>

      {grouped.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-black/60">
          No item sales are available for this business day yet.
        </p>
      ) : (
        <div className="max-h-[600px] overflow-auto divide-y divide-gray-200">
          {grouped.map(([dept, deptItems]) => {
            const deptTotal = deptItems.reduce((s, i) => s + i.gross_amount, 0);
            return (
              <div key={dept}>
                <div className="bg-gray-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-black/60">
                  {dept} Item Sales
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 bg-white text-left text-xs font-semibold uppercase tracking-wide text-black/70">
                      <th className="px-2 py-2 w-8 text-center">#</th>
                      <th className="px-4 py-2">Menu Item</th>
                      <th className="px-4 py-2 text-right">Unit Price</th>
                      <th className="px-4 py-2 text-right">Qty</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deptItems.map((item, idx) => (
                      <tr
                        key={item.id}
                        className="border-b border-gray-100 last:border-b-0"
                      >
                        <td className="px-2 py-2 text-center text-xs text-black/50">
                          {idx + 1}
                        </td>
                        <td className="px-4 py-2 font-medium text-black">
                          {item.item_name}
                        </td>
                        <td className="px-4 py-2 text-right text-black/80">
                          {formatAmount(item.unit_price)}
                        </td>
                        <td className="px-4 py-2 text-right text-black/80">
                          {formatAmount(item.quantity_sold)}
                        </td>
                        <td className="px-4 py-2 text-right font-semibold text-black">
                          {formatAmount(item.gross_amount)}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-gray-100">
                      <td
                        colSpan={4}
                        className="px-4 py-2 text-right text-xs font-bold uppercase tracking-wide text-black/70"
                      >
                        Total
                      </td>
                      <td className="px-4 py-2 text-right text-sm font-bold text-black">
                        {formatAmount(deptTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

const AccommodationDailySection: React.FC<{
  accommodation: OutletSalesReport['accommodation'] & object;
  businessDate: string;
}> = ({ accommodation, businessDate }) => {
  const fmtDate = (iso: string) => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? iso
      : d.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        });
  };

  const paymentsTotal = (accommodation.payments ?? []).reduce(
    (s, p) => s + p.amount,
    0,
  );
  const occupiedBillTotal = (accommodation.occupiedBookings ?? []).reduce(
    (s, b) => s + b.total_amount,
    0,
  );

  return (
    <section className="overflow-hidden rounded-md border border-gray-200">
      <div className="app-bg border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
          {fmtDate(businessDate)} Booking Payments
        </h3>
        <p className="mt-0.5 text-xs text-black/70">
          Payments received for accommodation bookings on this business day.
        </p>
      </div>

      <div className="overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-white text-left text-xs font-semibold uppercase tracking-wide text-black/70">
              <th className="px-4 py-2">Client</th>
              <th className="px-4 py-2">Check-In</th>
              <th className="px-4 py-2">Check-Out</th>
              <th className="px-4 py-2">Room</th>
              <th className="px-4 py-2">Payment Mode</th>
              <th className="px-4 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {(accommodation.payments ?? []).length > 0 ? (
              <>
                {(accommodation.payments ?? []).map((p) => (
                  <tr
                    key={p.id}
                    className="border-b border-gray-100 last:border-b-0"
                  >
                    <td className="px-4 py-2 font-medium text-black">
                      {p.client_name}
                    </td>
                    <td className="px-4 py-2 text-black/80">{p.check_in}</td>
                    <td className="px-4 py-2 text-black/80">{p.check_out}</td>
                    <td className="px-4 py-2 text-black/80">
                      {p.room_display}
                    </td>
                    <td className="px-4 py-2 text-black/80">
                      {p.payment_method_name}
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-black">
                      {formatAmount(p.amount)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-100">
                  <td
                    colSpan={5}
                    className="px-4 py-2 text-right text-xs font-bold uppercase tracking-wide text-black/70"
                  >
                    Total
                  </td>
                  <td className="px-4 py-2 text-right text-sm font-bold text-black">
                    {formatAmount(paymentsTotal)}
                  </td>
                </tr>
              </>
            ) : (
              <tr>
                <td
                  colSpan={6}
                  className="px-4 py-8 text-center text-sm text-black/60"
                >
                  No accommodation payments were recorded for this business day.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="border-t border-gray-200">
        <div className="app-bg px-4 py-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
            {accommodation.occupiedCount.toLocaleString()} Room
            {accommodation.occupiedCount === 1 ? '' : 's'} Occupied as of{' '}
            {fmtDate(businessDate)}
          </h3>
          <p className="mt-0.5 text-xs text-black/70">
            Active bookings currently checked in.
          </p>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-white text-left text-xs font-semibold uppercase tracking-wide text-black/70">
                <th className="px-4 py-2">Client</th>
                <th className="px-4 py-2">Check-In</th>
                <th className="px-4 py-2">Check-Out</th>
                <th className="px-4 py-2 text-center">Adults</th>
                <th className="px-4 py-2 text-center">Kids</th>
                <th className="px-4 py-2">Room</th>
                <th className="px-4 py-2 text-right">Total Bill</th>
                <th className="px-4 py-2 text-right">Daily Rate</th>
              </tr>
            </thead>
            <tbody>
              {(accommodation.occupiedBookings ?? []).length > 0 ? (
                <>
                  {(accommodation.occupiedBookings ?? []).map((b) => (
                    <tr
                      key={b.id}
                      className="border-b border-gray-100 last:border-b-0"
                    >
                      <td className="px-4 py-2 font-medium text-black">
                        {b.client_name}
                      </td>
                      <td className="px-4 py-2 text-black/80">{b.check_in}</td>
                      <td className="px-4 py-2 text-black/80">{b.check_out}</td>
                      <td className="px-4 py-2 text-center text-black/80">
                        {b.adults}
                      </td>
                      <td className="px-4 py-2 text-center text-black/80">
                        {b.children}
                      </td>
                      <td className="px-4 py-2 text-black/80">
                        {b.room_display}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold text-black">
                        {formatAmount(b.total_amount)}
                      </td>
                      <td className="px-4 py-2 text-right text-black/80">
                        {formatAmount(b.price_per_night)}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-100">
                    <td
                      colSpan={6}
                      className="px-4 py-2 text-right text-xs font-bold uppercase tracking-wide text-black/70"
                    >
                      Total
                    </td>
                    <td className="px-4 py-2 text-right text-sm font-bold text-black">
                      {formatAmount(occupiedBillTotal)}
                    </td>
                    <td aria-label="Daily rate total" />
                  </tr>
                </>
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-sm text-black/60"
                  >
                    No rooms are currently occupied.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

interface ColumnDef<Row> {
  header: string;
  render: (row: Row) => string;
  right?: boolean;
}

function CreditorLikeSection<Row>({
  title,
  subtitle,
  rows,
  columns,
  rowKey,
  emptyMessage,
}: {
  title: string;
  subtitle: string;
  rows: Row[];
  columns: ColumnDef<Row>[];
  rowKey: (row: Row) => string;
  emptyMessage: string;
}) {
  return (
    <section className="overflow-hidden rounded-md border border-gray-200">
      <div className="app-bg border-b border-gray-200 px-4 py-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
          {title}
        </h3>
        <p className="mt-0.5 text-xs text-black/70">{subtitle}</p>
      </div>
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-gray-200 bg-white text-left text-xs font-semibold uppercase tracking-wide text-black/70">
              {columns.map((col) => (
                <th
                  key={col.header}
                  className={`px-4 py-3${col.right ? ' text-right' : ''}`}
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row) => (
                <tr
                  key={rowKey(row)}
                  className="border-b border-gray-100 align-top last:border-b-0"
                >
                  {columns.map((col, i) => (
                    <td
                      key={col.header}
                      className={`px-4 py-3${col.right ? ' text-right font-semibold text-black' : i === 0 ? ' font-medium text-black' : ' text-black/80'}`}
                    >
                      {col.render(row)}
                    </td>
                  ))}
                </tr>
              ))
            ) : emptyMessage ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-sm text-black/60"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const hasSales = (report: OutletSalesReport | null): boolean => {
  if (!report) return false;

  return (
    report.summary.total_sale > 0 ||
    report.paymentMethods.length > 0 ||
    report.departments.length > 0 ||
    report.items.length > 0 ||
    report.settlements.length > 0 ||
    report.discounts.length > 0 ||
    report.creditors.length > 0 ||
    report.orders.length > 0 ||
    Boolean(report.accommodation?.roomRevenue)
  );
};

export const SalesReportModal: React.FC<SalesReportModalProps> = ({
  isOpen,
  report,
  outletName,
  businessDayLabel,
  businessDate,
  isLoading = false,
  isExporting = false,
  isSendingEmail = false,
  onClose,
  onExport,
  onSendEmail,
}) => {
  const resolvedBusinessDayLabel = useMemo(
    () => businessDayLabel || formatBusinessDayLabel(businessDate),
    [businessDate, businessDayLabel],
  );

  const accommodationTotal = useMemo(() => {
    return report?.accommodation?.roomRevenue ?? 0;
  }, [report]);

  const summaryCards = useMemo(() => {
    if (!report) return [];

    return [
      {
        label: 'Total Sale',
        value: formatAmount(report.summary.total_sale + accommodationTotal),
        hint: 'Net sale after discounts',
      },
      {
        label: 'Gross Sale',
        value: formatAmount(report.summary.gross_sale + accommodationTotal),
        hint: 'Before discounts',
      },
      {
        label: 'Discounts',
        value: formatAmount(report.summary.total_discount),
        hint: 'All discounts applied',
      },
      {
        label: 'Bills Settled',
        value: report.summary.settled_order_count.toLocaleString(),
        hint: `${report.summary.settlement_count.toLocaleString()} settlement entr${
          report.summary.settlement_count === 1 ? 'y' : 'ies'
        }`,
      },
    ];
  }, [report, accommodationTotal]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div
        className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
        onClick={isExporting ? undefined : onClose}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && !isExporting) onClose();
        }}
        role="button"
        tabIndex={0}
        aria-label="Close sales report modal"
      />

      <div className="relative flex max-h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-md bg-white text-black shadow-2xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
          <div>
            <h2 className="text-xl font-semibold text-black">Sales Report</h2>
            <p className="mt-1 text-sm text-black/70">
              {outletName} | Business day {resolvedBusinessDayLabel}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              isLoading={isExporting}
              disabled={isLoading || !report || isSendingEmail}
              onClick={onExport}
              className="border-none bg-primary text-white hover:bg-primary/90"
            >
              <Download size={16} />
              Export PDF
            </Button>

            {onSendEmail && (
              <Button
                type="button"
                size="sm"
                isLoading={isSendingEmail}
                disabled={isLoading || !report || isExporting}
                onClick={onSendEmail}
                className="border-none bg-white text-gray-700 hover:bg-gray-50 border-gray-200"
                variant="outline"
              >
                <Mail size={16} />
                Send Email
              </Button>
            )}

            <button
              type="button"
              onClick={onClose}
              disabled={isExporting}
              className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black disabled:opacity-50"
              aria-label="Close sales report modal"
            >
              <X size={22} />
            </button>
          </div>
        </div>

        <div className="custom-scrollbar space-y-6 overflow-y-auto px-6 py-6">
          {isLoading ? (
            <LoadingAnimation message="Loading outlet sales report..." />
          ) : null}

          {!isLoading && report ? (
            <>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {summaryCards.map((card) => (
                  <div
                    key={card.label}
                    className="app-bg rounded-md border border-gray-200 px-4 py-4"
                  >
                    <p className="text-xs font-semibold uppercase tracking-wide text-black/60">
                      {card.label}
                    </p>
                    <p className="mt-2 text-2xl font-semibold text-black">
                      {card.value}
                    </p>
                    <p className="mt-1 text-xs text-black/70">{card.hint}</p>
                  </div>
                ))}
              </div>

              <div className="grid gap-6 xl:grid-cols-[1.1fr,0.9fr]">
                <section className="overflow-hidden rounded-md border border-gray-200">
                  <div className="app-bg flex items-center justify-between border-b border-gray-200 px-4 py-3">
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
                        Settlement Breakdown
                      </h3>
                      <p className="mt-0.5 text-xs text-black/70">
                        Payment method totals for the current business day.
                      </p>
                    </div>
                    <span className="text-xs font-medium text-black/70">
                      Avg bill{' '}
                      {formatAmount(report.summary.average_bill_amount)}
                    </span>
                  </div>

                  <div className="max-h-72 overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 z-10">
                        <tr className="border-b border-gray-200 bg-white text-left text-xs font-semibold uppercase tracking-wide text-black/70">
                          <th className="px-4 py-3">Method</th>
                          <th className="px-4 py-3 text-right">Settlements</th>
                          <th className="px-4 py-3 text-right">Bills</th>
                          <th className="px-4 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {report.paymentMethods.length > 0 ? (
                          <>
                            {report.paymentMethods.map((entry) => (
                              <tr
                                key={`${entry.payment_method_id}-${entry.payment_method_name}`}
                                className="border-b border-gray-100 last:border-b-0"
                              >
                                <td className="px-4 py-3 font-medium text-black">
                                  {entry.payment_method_name}
                                </td>
                                <td className="px-4 py-3 text-right text-black/80">
                                  {entry.settlement_count.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-right text-black/80">
                                  {entry.settled_order_count.toLocaleString()}
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-black">
                                  {formatAmount(entry.total_amount)}
                                </td>
                              </tr>
                            ))}
                            {accommodationTotal > 0 ? (
                              <tr className="border-b border-gray-100 last:border-b-0 bg-gray-50/50">
                                <td className="px-4 py-3 font-medium text-black">
                                  Accommodation
                                </td>
                                <td className="px-4 py-3 text-right text-black/50">
                                  —
                                </td>
                                <td className="px-4 py-3 text-right text-black/50">
                                  —
                                </td>
                                <td className="px-4 py-3 text-right font-semibold text-black">
                                  {formatAmount(accommodationTotal)}
                                </td>
                              </tr>
                            ) : null}
                          </>
                        ) : (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-4 py-8 text-center text-sm text-black/60"
                            >
                              No settlement activity has been recorded for this
                              business day yet.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </section>

                <section className="overflow-hidden rounded-md border border-gray-200">
                  <div className="app-bg flex items-center justify-between border-b border-gray-200 px-4 py-3">
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
                        Report Snapshot
                      </h3>
                      <p className="mt-0.5 text-xs text-black/70">
                        High level open-day sales metrics.
                      </p>
                    </div>
                    <ReceiptText size={18} className="text-black/60" />
                  </div>

                  <div className="space-y-3 px-4 py-4 text-sm">
                    <div className="flex items-center justify-between border-b border-dashed border-gray-200 pb-2">
                      <span className="text-black/70">FNB</span>
                      <span className="font-semibold text-black">
                        {formatAmount(report.summary.gross_sale)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-dashed border-gray-200 pb-2">
                      <span className="text-black/70">Discounts</span>
                      <span className="font-semibold text-black">
                        {formatAmount(report.summary.total_discount)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-b border-dashed border-gray-200 pb-2">
                      <span className="text-black/70">
                        Total settlement amount
                      </span>
                      <span className="font-semibold text-black">
                        {formatAmount(report.summary.total_settlement_amount)}
                      </span>
                    </div>
                    {accommodationTotal > 0 && (
                      <div className="flex items-center justify-between border-b border-dashed border-gray-200 pb-2">
                        <span className="text-black/70">Accommodation</span>
                        <span className="font-semibold text-black">
                          {formatAmount(accommodationTotal)}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between border-b border-dashed border-gray-200 pb-2">
                      <span className="text-black/70">Bills settled</span>
                      <span className="font-semibold text-black">
                        {report.summary.settled_order_count.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-1 text-base">
                      <span className="font-semibold text-black">
                        Final sale
                      </span>
                      <span className="font-semibold text-black">
                        {formatAmount(
                          report.summary.total_sale + accommodationTotal,
                        )}
                      </span>
                    </div>
                  </div>
                </section>
              </div>

              <section className="overflow-hidden rounded-md border border-gray-200">
                <div className="app-bg border-b border-gray-200 px-4 py-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
                    Department Wise Sale
                  </h3>
                  <p className="mt-0.5 text-xs text-black/70">
                    Sales broken down by department. Cost fields are excluded.
                  </p>
                </div>

                <div className="max-h-72 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className="border-b border-gray-200 bg-white text-left text-xs font-semibold uppercase tracking-wide text-black/70">
                        <th className="px-4 py-3">Department</th>
                        <th className="px-4 py-3 text-right">Qty</th>
                        <th className="px-4 py-3 text-right">Items</th>
                        <th className="px-4 py-3 text-right">Gross</th>
                        <th className="px-4 py-3 text-right">Discount</th>
                        <th className="px-4 py-3 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.departments.length > 0 ? (
                        report.departments.map((department) => (
                          <tr
                            key={
                              department.department_id ||
                              department.department_name
                            }
                            className="border-b border-gray-100 last:border-b-0"
                          >
                            <td className="px-4 py-3 font-medium text-black">
                              {department.department_name}
                            </td>
                            <td className="px-4 py-3 text-right text-black/80">
                              {formatAmount(department.quantity_sold)}
                            </td>
                            <td className="px-4 py-3 text-right text-black/80">
                              {department.item_count.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-black/80">
                              {formatAmount(department.gross_sale)}
                            </td>
                            <td className="px-4 py-3 text-right text-black/80">
                              {formatAmount(department.total_discount)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-black">
                              {formatAmount(department.total_sale)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={6}
                            className="px-4 py-8 text-center text-sm text-black/60"
                          >
                            No department sales are available for this business
                            day yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <ItemSalesByDepartmentSection items={report.items} />

              <section className="overflow-hidden rounded-md border border-gray-200">
                <div className="app-bg border-b border-gray-200 px-4 py-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
                    Discounts Issued
                  </h3>
                  <p className="mt-0.5 text-xs text-black/70">
                    Bills that received discounts and why they were issued.
                  </p>
                </div>

                <div className="max-h-72 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className="border-b border-gray-200 bg-white text-left text-xs font-semibold uppercase tracking-wide text-black/70">
                        <th className="px-4 py-3">Bill</th>
                        <th className="px-4 py-3">Order</th>
                        <th className="px-4 py-3">Reason</th>
                        <th className="px-4 py-3">Added By</th>
                        <th className="px-4 py-3 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.discounts.length > 0 ? (
                        report.discounts.map((discount) => (
                          <tr
                            key={discount.id}
                            className="border-b border-gray-100 align-top last:border-b-0"
                          >
                            <td className="px-4 py-3 font-semibold text-black">
                              #{discount.bill_number}
                            </td>
                            <td className="px-4 py-3 text-black/80">
                              <p className="font-medium text-black">
                                {formatLocation(discount)}
                              </p>
                              {discount.client_name ? (
                                <p className="mt-1 text-xs text-black/60">
                                  {discount.client_name}
                                </p>
                              ) : null}
                            </td>
                            <td className="px-4 py-3 text-black/80">
                              {discount.notes || 'No reason provided'}
                            </td>
                            <td className="px-4 py-3 text-black/80">
                              {formatUserName(discount.added_by)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-black">
                              {formatAmount(discount.amount)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={5}
                            className="px-4 py-8 text-center text-sm text-black/60"
                          >
                            No discounts were issued for this business day.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* Item Cancellations — only shown if data exists */}
              {(report.itemCancellations ?? []).length > 0 ? (
                <CreditorLikeSection<ItemCancellationView>
                  title="Item Cancellations"
                  subtitle="Items removed from confirmed orders."
                  rows={report.itemCancellations}
                  columns={[
                    { header: 'Bill', render: (r) => `#${r.bill_number}` },
                    { header: 'Item', render: (r) => r.item_name },
                    {
                      header: 'Reason',
                      render: (r) => r.reason || 'No reason',
                    },
                    {
                      header: 'Cancelled By',
                      render: (r) =>
                        [r.cancelled_by?.first_name, r.cancelled_by?.last_name]
                          .filter(Boolean)
                          .join(' ') || '—',
                    },
                    {
                      header: 'Time',
                      render: (r) => formatSettlementTime(r.created_at),
                    },
                    {
                      header: 'Price',
                      render: (r) => formatAmount(r.unit_price),
                      right: true,
                    },
                  ]}
                  rowKey={(r) => r.id}
                  emptyMessage=""
                />
              ) : null}

              {/* NC Items */}
              {report.creditors.filter((c) => c.payment_method_name === 'NC')
                .length > 0 ? (
                <CreditorLikeSection<SalesReportCreditorView>
                  title="NC Items"
                  subtitle="Bills settled as No Charge."
                  rows={report.creditors.filter(
                    (c) => c.payment_method_name === 'NC',
                  )}
                  columns={[
                    { header: 'Type', render: (r) => r.payment_method_name },
                    { header: 'Bill', render: (r) => `#${r.bill_number}` },
                    {
                      header: 'Client',
                      render: (r) => r.client_name || 'No client',
                    },
                    { header: 'Order', render: (r) => formatLocation(r) },
                    { header: 'Notes', render: (r) => r.notes || '—' },
                    {
                      header: 'Amount',
                      render: (r) => formatAmount(r.amount),
                      right: true,
                    },
                  ]}
                  rowKey={(r) => r.id}
                  emptyMessage=""
                />
              ) : null}

              {/* Bill Cancellations */}
              {report.orders.filter((o) => o.status_name === 'CANCELLED')
                .length > 0 ? (
                <CreditorLikeSection<SalesReportOrderView>
                  title="Bill Cancellations"
                  subtitle="Orders that were cancelled for this business day."
                  rows={report.orders.filter(
                    (o) => o.status_name === 'CANCELLED',
                  )}
                  columns={[
                    {
                      header: 'Time',
                      render: (r) => formatSettlementTime(r.created_at),
                    },
                    { header: 'Bill', render: (r) => `#${r.bill_number}` },
                    { header: 'Location', render: (r) => formatLocation(r) },
                    {
                      header: 'Client / Waiter',
                      render: (r) => formatOrderParty(r),
                    },
                    {
                      header: 'Total',
                      render: (r) => formatAmount(r.net_amount),
                      right: true,
                    },
                  ]}
                  rowKey={(r) => r.order_id}
                  emptyMessage=""
                />
              ) : null}

              {/* Pending Orders */}
              {report.orders.filter(
                (o) => o.status_name === 'PENDING' || o.status_name === 'OPEN',
              ).length > 0 ? (
                <CreditorLikeSection<SalesReportOrderView>
                  title="Pending Orders"
                  subtitle="Orders still open or pending settlement."
                  rows={report.orders.filter(
                    (o) =>
                      o.status_name === 'PENDING' || o.status_name === 'OPEN',
                  )}
                  columns={[
                    {
                      header: 'Time',
                      render: (r) => formatSettlementTime(r.created_at),
                    },
                    { header: 'Bill', render: (r) => `#${r.bill_number}` },
                    { header: 'Location', render: (r) => formatLocation(r) },
                    {
                      header: 'Client / Waiter',
                      render: (r) => formatOrderParty(r),
                    },
                    {
                      header: 'Total',
                      render: (r) => formatAmount(r.net_amount),
                      right: true,
                    },
                  ]}
                  rowKey={(r) => r.order_id}
                  emptyMessage=""
                />
              ) : null}

              {/* Credit Bills */}
              {report.creditors.filter(
                (c) => c.payment_method_name === 'CREDIT',
              ).length > 0 ? (
                <CreditorLikeSection<SalesReportCreditorView>
                  title="Credit Bills"
                  subtitle="Bills settled on credit."
                  rows={report.creditors.filter(
                    (c) => c.payment_method_name === 'CREDIT',
                  )}
                  columns={[
                    { header: 'Type', render: (r) => r.payment_method_name },
                    { header: 'Bill', render: (r) => `#${r.bill_number}` },
                    {
                      header: 'Client',
                      render: (r) => r.client_name || 'No client',
                    },
                    { header: 'Order', render: (r) => formatLocation(r) },
                    { header: 'Notes', render: (r) => r.notes || '—' },
                    {
                      header: 'Amount',
                      render: (r) => formatAmount(r.amount),
                      right: true,
                    },
                  ]}
                  rowKey={(r) => r.id}
                  emptyMessage=""
                />
              ) : null}

              <section className="overflow-hidden rounded-md border border-gray-200">
                <div className="app-bg border-b border-gray-200 px-4 py-3">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-black">
                    Last 20 Orders
                  </h3>
                  <p className="mt-0.5 text-xs text-black/70">
                    Most recent orders created for the selected business day.
                  </p>
                </div>

                <div className="max-h-72 overflow-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 z-10">
                      <tr className="border-b border-gray-200 bg-white text-left text-xs font-semibold uppercase tracking-wide text-black/70">
                        <th className="px-4 py-3">Time</th>
                        <th className="px-4 py-3">Bill</th>
                        <th className="px-4 py-3">Order</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Client / Waiter</th>
                        <th className="px-4 py-3 text-right">Discount</th>
                        <th className="px-4 py-3 text-right">Net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.orders.length > 0 ? (
                        report.orders.map((order) => (
                          <tr
                            key={order.order_id}
                            className="border-b border-gray-100 align-top last:border-b-0"
                          >
                            <td className="px-4 py-3 text-black/80">
                              {formatSettlementTime(order.created_at)}
                            </td>
                            <td className="px-4 py-3 font-semibold text-black">
                              #{order.bill_number}
                            </td>
                            <td className="px-4 py-3 text-black/80">
                              <p className="font-medium text-black">
                                {formatLocation(order)}
                              </p>
                              <p className="mt-1 text-xs text-black/60">
                                {order.item_count.toLocaleString()} item entr
                                {order.item_count === 1 ? 'y' : 'ies'}
                              </p>
                            </td>
                            <td className="px-4 py-3 text-black/80">
                              {order.status_name}
                            </td>
                            <td className="px-4 py-3 text-black/80">
                              {formatOrderParty(order)}
                            </td>
                            <td className="px-4 py-3 text-right text-black/80">
                              {formatAmount(order.discount_amount)}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-black">
                              {formatAmount(order.net_amount)}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-8 text-center text-sm text-black/60"
                          >
                            No orders were created for this business day.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              {report.accommodation ? (
                <AccommodationDailySection
                  accommodation={report.accommodation}
                  businessDate={businessDate ?? ''}
                />
              ) : null}

              {!hasSales(report) ? (
                <div className="rounded-md border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-black/60">
                  No sales data is available for this business day yet.
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};
